import { formatDateRu, formatShiftWindow } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { db } from '../sync/db.js';

/**
 * Личный график. Собирается из локальной базы, поэтому виден и без сети —
 * а в цеху это обычное дело.
 */
export function MyScheduleScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);

  const rows = useLiveQuery(
    async () => {
      if (!user) return [];
      const assignments = await db.shiftAssignments.where('userId').equals(user.id).toArray();
      const result = [];

      for (const assignment of assignments) {
        const shift = await db.shifts.get(assignment.shiftId);
        if (!shift) continue;
        const [site, machine, templates] = await Promise.all([
          db.sites.get(shift.siteId),
          db.machines.get(assignment.machineId),
          db.shiftTemplates.where('siteId').equals(shift.siteId).toArray(),
        ]);
        const template = templates.find(
          (item) => item.shiftNumber === shift.shiftNumber && item.validTo === null,
        );

        result.push({
          id: assignment.id,
          date: shift.date,
          shiftNumber: shift.shiftNumber,
          siteName: site?.name ?? 'участок',
          machineName: machine?.name ?? '',
          status: assignment.status,
          window: template
            ? formatShiftWindow({
                shiftNumber: template.shiftNumber,
                startMinute: template.startMinute,
                durationMinutes: template.durationMinutes,
              })
            : '',
        });
      }

      return result.sort((a, b) => a.date.localeCompare(b.date) || a.shiftNumber - b.shiftNumber);
    },
    [user?.id],
    [],
  );

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = rows.filter((row) => row.date >= today);

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Мой график</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {upcoming.length === 0 ? (
        <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
          Смен впереди нет. Как только график опубликуют, он появится здесь.
        </p>
      ) : (
        <ul className="flex flex-col gap-touch">
          {upcoming.map((row) => (
            <li key={row.id} className="rounded-xl bg-card shadow-card p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-lg font-semibold">{formatDateRu(row.date)}</span>
                <span className="text-base text-dim">
                  {row.shiftNumber}-я смена {row.window}
                </span>
              </div>
              <p className="mt-1 text-base text-body">
                {row.siteName} · {row.machineName}
              </p>
              {row.status !== 'ASSIGNED' && (
                <p className="mt-2 text-base text-amber-700">
                  {row.status === 'ABSENT_PLANNED' && 'Отсутствие согласовано'}
                  {row.status === 'ABSENT_UNPLANNED' && 'Отмечен невыход'}
                  {row.status === 'REPLACED' && 'Заменён'}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <Link
        to="/absence"
        className="mt-6 block rounded-xl bg-muted px-5 py-4 text-center text-lg font-semibold active:bg-line"
      >
        Не выйду на смену
      </Link>
    </div>
  );
}
